import type { PillarKey } from "@/content/makechange";

/**
 * Daily pillar content for the MAKE CHANGE NOW app.
 * DRAFT — written from Dr. Sully's books (Built On Purpose, Time to Heal,
 * Honest in the Dark, CEO 000 000) for his review before it's final.
 */
export type PillarDay = { source: string; teaching: string; reflection: string; action: string };

export const PILLAR_DAILY: Record<PillarKey, PillarDay> = {
  momentum: {
    source: "MAKECHANGE Framework",
    teaching:
      "You disrupt stagnation and create forward motion. Momentum is the catalyst for breaking through barriers. By leveraging both spiritual discipline and AI-powered efficiency tools, leaders can accelerate their progress and maintain a steady pace of growth without burnout.",
    reflection: "Where have I been waiting for permission I don't actually need?",
    action: "Spend 15 focused minutes on the one task you've postponed the longest.",
  },
  authority: {
    source: "MAKECHANGE Framework",
    teaching:
      "Spiritual and professional authority grounded in biblical truth. True authority is not demanded; it is demonstrated. As Proverbs 29:2 (KJV) states, \"When the righteous are in authority, the people rejoice: but when the wicked beareth rule, the people mourn.\" We lead with integrity, utilizing data and AI insights to make authoritative, well-informed decisions that honor God and serve the community.",
    reflection: "Where is there a gap between what I say and what I actually do?",
    action: "Make one decision today backed by a fact you checked, not an assumption.",
  },
  kingdom: {
    source: "MAKECHANGE Framework",
    teaching:
      "Kingdom mindset, eternal truths, ethical integrity. Every strategy, whether it involves traditional leadership or implementing AI systems, must align with Kingdom principles. We prioritize ethical AI usage and ensure that all technological advancements serve to expand the Kingdom and uplift others.",
    reflection: "What decision in front of me would look different if I measured it by eternity?",
    action: "Before one key decision today, pause and pray or reflect for two minutes first.",
  },
  equity: {
    source: "MAKECHANGE Framework",
    teaching:
      "Fair and accessible systems. Equity means creating environments where everyone has the opportunity to succeed. By integrating AI, we can identify and dismantle systemic biases, ensuring fair access to resources and coaching for all individuals, regardless of their background.",
    reflection: "Who is my current system quietly leaving out?",
    action: "Ask one person who is usually unheard for their honest input today.",
  },
  clarity: {
    source: "MAKECHANGE Framework",
    teaching:
      "\"If it doesn't make sense — make change.\" Cut through noise. Clarity is essential for effective leadership. We utilize AI-driven analytics to filter out distractions and pinpoint actionable insights, allowing leaders to focus on what truly matters and make decisive changes.",
    reflection: "If I could only get one thing done this week, what would it be?",
    action: "Write your top priority on one line and cross off one thing that doesn't serve it.",
  },
  healing: {
    source: "MAKECHANGE Framework",
    teaching:
      "Trauma-informed coaching, heal past wounds to lead with resilience. Healing is a prerequisite for sustainable leadership. We address past traumas through compassionate coaching, creating a resilient foundation that allows leaders to adapt to rapid changes, including the integration of new technologies like AI. If you are in crisis, call or text 988.",
    reflection: "What pain am I still carrying that is shaping how I lead or love?",
    action: "Name one hurt honestly — in writing, in prayer, or to someone you trust.",
  },
  action: {
    source: "MAKECHANGE Framework",
    teaching:
      "Actionable steps, scalable strategies tied to growth and revenue. Faith without works is dead (James 2:20, KJV). We translate vision into execution by developing scalable strategies. AI tools are employed to automate repetitive tasks, freeing up leaders to focus on high-impact actions that drive revenue and expand their reach.",
    reflection: "What plan have I talked about more than I've worked on?",
    action: "Finish one concrete task that moves money, mission or ministry forward.",
  },
  navigation: {
    source: "MAKECHANGE Framework",
    teaching:
      "Guide leaders through complex transitions. The landscape of leadership is rapidly evolving. We provide expert guidance through organizational changes and the AI revolution, ensuring leaders are not left behind but are equipped to harness AI as a powerful tool for navigation and strategic planning.",
    reflection: "What transition am I in, and what is the next small step I can see?",
    action: "Take one step today on a change you've been avoiding — learn, ask, or try it.",
  },
  growth: {
    source: "MAKECHANGE Framework",
    teaching:
      "Cultivate development in students, men, and leaders. Growth is intentional and continuous. Through mentorship programs like The Forge SA and SPARK Club, we foster personal and professional development, teaching the next generation to lead with both spiritual wisdom and technological fluency.",
    reflection: "Who am I intentionally pouring into right now?",
    action: "Teach, encourage or open a door for one person coming up behind you.",
  },
  empowerment: {
    source: "MAKECHANGE Framework",
    teaching:
      "Build capacity, not dependency. Leave people equipped. The ultimate goal is to empower others to lead independently. We provide the tools, including AI resources and strategic frameworks, to build lasting capacity, ensuring that leaders can continue to thrive and empower others long after the coaching relationship has ended.",
    reflection: "Where am I doing something for someone that they could learn to do themselves?",
    action: "Hand off one task today — with clear expectations and your trust.",
  },
};

/** Lowest three pillars from a scan; ties keep framework order. */
export const focusFromScores = (scores: Partial<Record<PillarKey, number>>, order: readonly PillarKey[]) =>
  order
    .filter((k) => typeof scores[k] === "number")
    .sort((a, b) => (scores[a]! - scores[b]!) || order.indexOf(a) - order.indexOf(b))
    .slice(0, 3);
